import { differenceInMinutes, endOfWeek, format, parseISO, startOfWeek } from 'date-fns';

import { type DataList } from '../Timesheet/reducer';

export interface EmployeeSummary {
  employeeId: number;
  employeeName: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  weekStart: string;
  weekEnd: string;
}

export const calculateWeeklySummary = (data: DataList): EmployeeSummary[] => {
  const weeklyEmployeeSummaries = new Map<string, Map<number, EmployeeSummary>>();

  Object.values(data).forEach((dayData) => {
    dayData.timesheetLineList.forEach((line) => {
      if (!line.technicianUserId || !line.timeStarted || !line.timeFinished) return;

      const employeeId = line.technicianUserId;
      const employeeName = line.technicianUserName || 'Unknown';
      const timeStarted = parseISO(line.timeStarted);

      // Get week start (Saturday) and end (Friday) for the timesheet
      const weekStartDate = startOfWeek(timeStarted, { weekStartsOn: 6 });
      const weekEndDate = endOfWeek(timeStarted, { weekStartsOn: 6 });
      const weekKey = `${format(weekStartDate, 'yyyy-MM-dd')}_${format(weekEndDate, 'yyyy-MM-dd')}`;

      const minutes = differenceInMinutes(parseISO(line.timeFinished), parseISO(line.timeStarted));
      const hours = minutes / 60;

      if (!weeklyEmployeeSummaries.has(weekKey)) {
        weeklyEmployeeSummaries.set(weekKey, new Map());
      }
      const weekSummaries = weeklyEmployeeSummaries.get(weekKey)!;

      const summary = weekSummaries.get(employeeId) || {
        employeeId,
        employeeName,
        regularHours: 0,
        overtimeHours: 0,
        totalHours: 0,
        weekStart: format(weekStartDate, 'yyyy-MM-dd'),
        weekEnd: format(weekEndDate, 'yyyy-MM-dd'),
      };

      summary.totalHours += hours;
      if (summary.totalHours <= 40) {
        summary.regularHours += hours;
      } else {
        const remainingRegular = Math.max(0, 40 - (summary.totalHours - hours));
        summary.regularHours += remainingRegular;
        summary.overtimeHours += hours - remainingRegular;
      }

      weekSummaries.set(employeeId, summary);
    });
  });

  // Convert the nested maps to a flat array of summaries
  const allSummaries: EmployeeSummary[] = [];
  weeklyEmployeeSummaries.forEach((weekSummaries) => {
    weekSummaries.forEach((summary) => {
      allSummaries.push(summary);
    });
  });

  // Sort by week start date and then by employee name
  return allSummaries.sort((a, b) => {
    const weekCompare = a.weekStart.localeCompare(b.weekStart);
    if (weekCompare !== 0) return weekCompare;
    return a.employeeName.localeCompare(b.employeeName);
  });
};
